import { Me } from './types/problem';
import { subscribe, verifySubscription } from './hooks/useApi';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void; on: (ev: string, fn: (r: any) => void) => void };
  }
}

let checkoutScript: Promise<void> | null = null;

function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (!checkoutScript) {
    checkoutScript = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve();
      s.onerror = () => {
        checkoutScript = null;
        reject(new Error('Could not load the payment window. Check your connection.'));
      };
      document.body.appendChild(s);
    });
  }
  return checkoutScript;
}

/**
 * Opens Razorpay checkout for a paid plan. Resolves with the updated account once
 * the server has verified the payment, or null if the user closed the window.
 */
export async function startCheckout(planKey: string): Promise<Me | null> {
  const [sub] = await Promise.all([subscribe(planKey), loadCheckout()]);

  return new Promise((resolve, reject) => {
    const rz = new window.Razorpay!({
      key: sub.keyId,
      subscription_id: sub.subscriptionId,
      name: 'CodeReviser',
      description: sub.label,
      prefill: { name: sub.name, email: sub.email },
      theme: { color: '#4f46e5' },
      handler: async (resp: Record<string, string>) => {
        try {
          resolve(await verifySubscription({ ...resp, plan: planKey }));
        } catch (err) {
          reject(err);
        }
      },
      modal: { ondismiss: () => resolve(null) },
    });
    rz.on('payment.failed', (r: any) => reject(new Error(r?.error?.description || 'Payment failed')));
    rz.open();
  });
}
